import React, { useEffect, useState } from "react";
import { ShoppingBag, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PRODUCTS } from "../../data/content";
import { useCart } from "../../context/CartContext";
import { ProductQuickView } from "./ProductQuickView";
import api from "../../lib/api";

const formatPrice = (amount, currency) => {
  if (amount == null) return "";
  const symbols = { USD: "$", GBP: "£", EUR: "€" };
  const sym = symbols[currency] || "";
  return `${sym}${Number(amount).toFixed(2)}${sym ? "" : " " + (currency || "")}`;
};

export const Shop = ({ settings }) => {
  const [collections, setCollections] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [active, setActive] = useState(null);
  const { loading } = useCart();

  useEffect(() => {
    if (settings?.shopify_enabled) {
      api
        .get("/shop/collections")
        .then((res) => {
          if (res.data.connected && res.data.collections?.length) setCollections(res.data.collections);
          else setCollections([]);
        })
        .catch(() => setCollections([]));
    }
  }, [settings]);

  const connected = Array.isArray(collections) && collections.length > 0;
  const activeCollection = connected ? collections[Math.min(activeTab, collections.length - 1)] : null;
  const live = activeCollection?.products || [];
  const shopUrl =
    settings?.shopify_collection_url ||
    (settings?.shopify_domain ? `https://${settings.shopify_domain.replace(/^https?:\/\//, "")}` : null);

  return (
    <section id="shop" data-testid="shop-section" className="bg-cream text-matte py-24 md:py-32 px-6 md:px-12 lg:px-16">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="eyebrow !text-matte/50 mb-4">The Merch</p>
            <h2 className="heading text-5xl sm:text-6xl lg:text-7xl text-matte">Wear The Journey.</h2>
            <p className="font-accent text-3xl text-matte/70 -rotate-2 mt-3">Different Route. Same Dream.</p>
          </div>
          {connected && shopUrl && (
            <a
              data-testid="shop-all-btn"
              href={shopUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-matte text-cream px-8 py-4 uppercase tracking-[0.2em] text-xs font-bold hover:bg-charcoal transition-colors"
            >
              Shop All <ArrowUpRight size={16} />
            </a>
          )}
        </div>
      </Reveal>

      {connected ? (
        <>
          {collections.length > 1 && (
            <Reveal>
              <div data-testid="collection-tabs" className="flex flex-wrap gap-3 mb-10">
                {collections.map((c, i) => (
                  <button
                    key={c.handle || i}
                    data-testid={`collection-tab-${i}`}
                    onClick={() => setActiveTab(i)}
                    className={`px-6 py-3 uppercase tracking-[0.18em] text-[0.7rem] font-bold border transition-colors ${
                      i === activeTab
                        ? "bg-matte text-cream border-matte"
                        : "bg-transparent text-matte/60 border-matte/25 hover:border-matte hover:text-matte"
                    }`}
                  >
                    {c.title}
                  </button>
                ))}
              </div>
            </Reveal>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {live.map((p, i) => (
              <Reveal key={p.handle || i} delay={i * 0.06}>
                <div data-testid={`product-${i}`} className="group flex flex-col">
                  <button
                    data-testid={`product-open-${i}`}
                    onClick={() => setActive(p)}
                    className="relative overflow-hidden aspect-[4/5] bg-white mb-4 block w-full text-left cursor-pointer"
                  >
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-matte text-cream flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ShoppingBag size={16} />
                    </div>
                  </button>
                  <h3 className="heading text-2xl text-matte">{p.title}</h3>
                  <p className="text-matte/60 text-sm mt-1 mb-3">{formatPrice(p.price, p.currency)}</p>
                  <button
                    data-testid={`add-to-cart-${i}`}
                    disabled={!p.available || loading}
                    onClick={() => setActive(p)}
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-matte text-cream px-5 py-3 uppercase tracking-[0.2em] text-[0.7rem] font-bold hover:bg-charcoal transition-colors disabled:opacity-40"
                  >
                    <ShoppingBag size={14} /> {p.available === false ? "Sold Out" : "Add To Bag"}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <div data-testid={`product-${i}`} className="group block">
                  <div className="relative overflow-hidden aspect-[4/5] bg-white mb-4">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-matte text-cream flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ShoppingBag size={16} />
                    </div>
                  </div>
                  <h3 className="heading text-2xl text-matte">{p.name}</h3>
                  <p className="text-matte/60 text-sm mt-1">{p.price}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p data-testid="shop-coming-soon" className="text-center text-matte/40 text-sm uppercase tracking-[0.2em] mt-14">
              Store launching soon · One Of None collection
            </p>
          </Reveal>
        </>
      )}
      {active && <ProductQuickView product={active} onClose={() => setActive(null)} />}
    </section>
  );
};
